'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';

const supabase = createClient();

const TIPI_STRUTTURA = [
  { value: 'laboratorio_analisi', label: 'Laboratorio Analisi' },
  { value: 'ospedale', label: 'Ospedale' },
  { value: 'poliambulatorio', label: 'Poliambulatorio' },
  { value: 'clinica_privata', label: 'Clinica Privata' },
  { value: 'asl', label: 'ASL' },
  { value: 'irccs', label: 'IRCCS' },
  { value: 'altro', label: 'Altro' },
];

const REGIONI = [
  'Abruzzo','Basilicata','Calabria','Campania','Emilia-Romagna','Friuli-Venezia Giulia',
  'Lazio','Liguria','Lombardia','Marche','Molise','Piemonte','Puglia','Sardegna',
  'Sicilia','Toscana','Trentino-Alto Adige','Umbria','Valle d\'Aosta','Veneto',
];

interface FormData {
  nome_struttura: string;
  tipo: string;
  codice_fiscale: string;
  indirizzo: string;
  citta: string;
  provincia: string;
  regione: string;
  asl_riferimento: string;
  nome_referente: string;
  cognome_referente: string;
  email_referente: string;
  telefono: string;
  note: string;
}

export default function RegistratiContent() {
  const [form, setForm] = useState<FormData>({
    nome_struttura: '',
    tipo: 'laboratorio_analisi',
    codice_fiscale: '',
    indirizzo: '',
    citta: '',
    provincia: '',
    regione: '',
    asl_riferimento: '',
    nome_referente: '',
    cognome_referente: '',
    email_referente: '',
    telefono: '',
    note: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.nome_struttura.trim()) { setError('Inserisci il nome della struttura.'); return; }
    if (!form.nome_referente.trim() || !form.cognome_referente.trim()) { setError('Inserisci nome e cognome del referente IT.'); return; }
    if (!form.email_referente.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_referente)) {
      setError('Inserisci un indirizzo email valido.'); return;
    }

    setLoading(true);
    try {
      const { error: dbError } = await supabase
        .from('richieste_strutture')
        .insert({
          nome_struttura: form.nome_struttura.trim(),
          tipo: form.tipo,
          codice_fiscale: form.codice_fiscale.trim(),
          indirizzo: form.indirizzo.trim(),
          citta: form.citta.trim(),
          provincia: form.provincia.trim(),
          regione: form.regione,
          asl_riferimento: form.asl_riferimento.trim(),
          nome_referente: form.nome_referente.trim(),
          cognome_referente: form.cognome_referente.trim(),
          email_referente: form.email_referente.trim().toLowerCase(),
          telefono: form.telefono.trim(),
          note: form.note.trim(),
          stato: 'in_attesa',
        });

      if (dbError) throw dbError;

      trackEvent('struttura_registrazione_inviata', {
        tipo: form.tipo,
        regione: form.regione,
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Errore durante l\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-lg text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
            <Icon name="CheckCircleIcon" size={40} className="text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white">Richiesta inviata!</h1>
          <p className="text-secondary-color text-lg">
            Grazie <strong className="text-white">{form.nome_referente}</strong>! La tua richiesta per{' '}
            <strong className="text-white">{form.nome_struttura}</strong> è stata ricevuta.
          </p>
          <p className="text-secondary-color">
            Il nostro team la esaminerà e ti contatterà all&apos;indirizzo{' '}
            <strong className="text-sky-400">{form.email_referente}</strong> entro 24 ore lavorative.
          </p>
          <div className="bg-surface border border-border rounded-2xl p-6 text-left space-y-3">
            <h3 className="text-white font-semibold">Cosa succede adesso?</h3>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
              <p className="text-sm text-secondary-color">Il nostro team verifica i dati della struttura</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
              <p className="text-sm text-secondary-color">Ricevi email con le credenziali di accesso</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
              <p className="text-sm text-secondary-color">Completi l&apos;onboarding e configuri la tua struttura</p>
            </div>
          </div>
          <Link href="/" className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 font-semibold transition-colors">
            <Icon name="ArrowLeftIcon" size={16} />
            Torna alla homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-10">
          <Link href="/" className="flex items-center gap-3 mb-4">
            <AppLogo size={40} />
            <span className="text-2xl font-bold text-white tracking-tight">GestioneLab</span>
          </Link>
          <h1 className="text-3xl font-bold text-white text-center">Registra la tua struttura</h1>
          <p className="text-secondary-color text-center mt-2 max-w-md">
            Accesso gratuito per tutte le strutture sanitarie italiane. Compila il modulo e ti contatteremo entro 24 ore.
          </p>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-8 shadow-xl">
          {error && (
            <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3 mb-6">
              <Icon name="ExclamationCircleIcon" size={18} className="text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-400 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dati Struttura */}
            <div>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="BuildingOffice2Icon" size={20} className="text-sky-400" />
                Dati Struttura
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Nome Struttura *</label>
                  <input
                    type="text"
                    name="nome_struttura"
                    value={form.nome_struttura}
                    onChange={handleChange}
                    placeholder="es. Laboratorio Analisi Ospedale Niguarda"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Tipo Struttura</label>
                    <select
                      name="tipo"
                      value={form.tipo}
                      onChange={handleChange}
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    >
                      {TIPI_STRUTTURA.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Codice Fiscale</label>
                    <input
                      type="text"
                      name="codice_fiscale"
                      value={form.codice_fiscale}
                      onChange={handleChange}
                      placeholder="12345678901"
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Indirizzo</label>
                  <input
                    type="text"
                    name="indirizzo"
                    value={form.indirizzo}
                    onChange={handleChange}
                    placeholder="Via Roma 1"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Città</label>
                    <input
                      type="text"
                      name="citta"
                      value={form.citta}
                      onChange={handleChange}
                      placeholder="Milano"
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Provincia</label>
                    <input
                      type="text"
                      name="provincia"
                      value={form.provincia}
                      onChange={handleChange}
                      placeholder="MI"
                      maxLength={2}
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Regione</label>
                    <select
                      name="regione"
                      value={form.regione}
                      onChange={handleChange}
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    >
                      <option value="">Seleziona</option>
                      {REGIONI.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">ASL di Riferimento</label>
                  <input
                    type="text"
                    name="asl_riferimento"
                    value={form.asl_riferimento}
                    onChange={handleChange}
                    placeholder="es. ATS Milano"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Referente IT */}
            <div>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="UserIcon" size={20} className="text-sky-400" />
                Referente IT
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Nome *</label>
                    <input
                      type="text"
                      name="nome_referente"
                      value={form.nome_referente}
                      onChange={handleChange}
                      placeholder="Mario"
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Cognome *</label>
                    <input
                      type="text"
                      name="cognome_referente"
                      value={form.cognome_referente}
                      onChange={handleChange}
                      placeholder="Rossi"
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Email *</label>
                    <input
                      type="email"
                      name="email_referente"
                      value={form.email_referente}
                      onChange={handleChange}
                      placeholder="mario.rossi@ospedale.it"
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-2">Telefono</label>
                    <input
                      type="tel"
                      name="telefono"
                      value={form.telefono}
                      onChange={handleChange}
                      placeholder="+39 02 1234567"
                      className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Note aggiuntive</label>
                  <textarea
                    name="note"
                    value={form.note}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Informazioni aggiuntive sulla struttura o esigenze specifiche..."
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Privacy note */}
            <p className="text-xs text-muted-color">
              Inviando questo modulo accetti che i tuoi dati vengano trattati per la gestione della richiesta di accesso a GestioneLab.
              I dati non verranno ceduti a terzi.
            </p>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-sky-500 hover:bg-sky-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={18} />
                  Invia Richiesta di Accesso
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-color mt-6">
          Hai già un account?{' '}
          <Link href="/login" className="text-sky-400 hover:text-sky-300 font-semibold transition-colors">
            Accedi
          </Link>
        </p>
      </div>
    </div>
  );
}
